import React, { useEffect, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import Helmet from 'react-helmet';

import { capitalize, debounce } from 'lodash';
import { ChevronDownIcon } from 'lucide-react';
import moment from 'moment';
import queryString from 'query-string';
import { Button, Input, DefaultPopover, Separator, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from 'twyne-new.ui';

import appStore from 'stores/app';
import { useQueryparams, formatDate } from 'utils';
import history from 'utils/history';

import DatesFilter from './Header'
import Table from './Table'
import Configure from './configure'
import { weekOfMonthFormatter, dateRangeFormatter } from './utils';
import { HeaderActions, HeaderSeparator, PermanentLinkButton, RunButton } from '@/components/reports/header';
import { ReportBody } from '../reports/body';
import HeaderContainerGrid from '../headerContainer/Grid';

export { formatters } from './utils'

export default class HierarchicalReportLayout extends React.Component {
  /**
   * @property {store: SingletonStore} - The default api store
   * @property {title: string} - The helmet title
   * @property {columns: array} - The table columns description
   * @property {summaryCards: array} - The summary cards data
   * @property {configure: object} - Sidebar configure description
   * @property {loadData: async function} - Funcion to call when load the data. Receives the configure state as parameter
   */

  state = {
    shownData: [],
    groupby: [],
    sortby: [],
    metrics: [],
    filterby: {},
    startDate: null,
    endDate: null,
    timezone: null,
    comparison: {},
  }

  useNewStore = () => ({})

  get isComparison() {
    return this.state.comparison?.compare
  }

  get csv() {
    const data = this.store.collection.data ? this.store.collection.data.children : []

    return [
      [...this.csvHeaders()],
      [...this.csvDataPlain(data)],
      [...this.csvDataNested(data)],
    ]
  }

  componentWillUnmount() {
    this.resetEntities()
    this.store.resetCollection()
  }

  resetEntities = () => {
    this.store.resetEntities()
  }

  loadData = async (params) => {
    params.detachedSite = this.props.detachedSite ? true : false

    if(params.groupby.includes('post') || params.groupby.includes('post_target')) {
      await this.store.post.fetchData();
    }
    await this.store.fetchData(params)
    this.setState({})
  }

  loadDataState = debounce(() => {
    this.loadData(this.state);
    this.setState({})
  }, 300)

  newTab = (event, metric, row) => {
    try {
      event.stopPropagation()

      const { filterby, startDate, endDate } = this.state

      const filters = {...filterby}
      const appendFilter = (row) => {
        const { parent } = row
        if (parent)
          appendFilter(parent)

        const { key, keyString, value } = row

        if (key.endsWith('_unittime')) {
          filters[key] = [keyString]
        } else if (['hour','day','week','month'].includes(key.split('_')[0])) {
          filters[key] = dateRangeFormatter(value, key.split('_')[0])
        } else {
          filters[key] = [value]
        }
      }
      appendFilter(row)

      if (!this.props.detachedSite)
        filters.site = [appStore.currentSiteID]

      const state = JSON.stringify({
        filters,
        startDate: startDate?.format?.(),
        endDate: endDate?.format?.(),
        metric,
      })
      const params = btoa(state)
      const search = queryString.stringify({ configure: params })

      const base = history.location.pathname
      window.open(history.createHref({ pathname: `${base}/drilldown`, search }))
    } catch (error) {
      console.log(error)
    }
  }

  handleChangeDates = state => {
    const { startDate, endDate, comparison } = state

    if (startDate && endDate) {
      return this.setState({
        startDate,
        endDate,
      }, this.loadDataState)
    }

    if (comparison) {
      return this.setState(state => ({
        ...state,
        comparison: {
          ...state.comparison,
          ...comparison,
        },
      }), this.loadDataState)
    }
  }

  handleChangeConfigure = (state) => {
    if (Object.keys(state.groupby).length == 0)
      return false
    
    if (state.filterby)
      state.filterby = Object.keys(state.filterby).reduce((filterby, field) => {
        const items = state.filterby[field].items.map(item => item.getValue())
        if (items.length > 0) filterby = { ...filterby, ...{ [field]: items } }
        return filterby
      }, {})
    this.setState(state, this.loadDataState)
    return state
  }

  handleChangeSelectConfigure = state => {
    this.setState(state, this.loadDataState)
  }

  Table = () => {
    return <Table key='table' columns={this.columns} data={this.store.collection.data} comparison={this.isComparison} onChange={shownData => this.setState({ shownData })} />
  }

  DatesButtons = () => {
    const { startDate, endDate, comparison } = this.state
    return <DatesFilter key='header'
      dates={{ startDate, endDate }}
      comparison={this.suppressCompare ? null : comparison}
      isLoading={this.store.collection.isLoading}
      onChange={this.handleChangeDates}
    />
  }

  convertValueCsvData(row) {
    if (row.key === 'month' || row.key === 'month_session') return formatDate(parseInt(row.value), 'MM/YYYY')
    if (row.key === 'day' || row.key === 'day_session') return formatDate(parseInt(row.value))
    if (row.key === 'hour' || row.key === 'hour_session') return formatDate(parseInt(row.value), 'MM/DD/YYYY [at] HH:mm')
    if (row.key === 'week' || row.key === 'week_session') return weekOfMonthFormatter(row.value)
    if (row.key === 'year') return formatDate(parseInt(row.value), 'YYYY')
    if (row.key === 'day_unittime') return formatDate(parseInt(row.value), '[Day] DD')
    if (row.key === 'hour_unittime') return formatDate(parseInt(row.value), 'HH [h]')
    if (row.key === 'month_unittime') return formatDate(parseInt(row.value), 'MMMM')
    if (row.key === 'weekday_unittime') return formatDate(parseInt(row.value), 'dddd')
    if (!row.name) return row.value

    return `${row.value}-${row.name}`
  }

  csvHeaders = function* () {
    const { columns: [_, ...columns], state } = this

    for (let group of state.groupby) {
      yield { label: capitalize(group), key: group }
    }

    for (let { dataField, text } of columns) {
      if (!dataField || !text) continue
      if (state.comparison?.compare) {
        yield { label: `${text} A`, key: `${dataField}A` }
        yield { label: `${text} B`, key: `${dataField}B` }
      } else {
        yield { label: text, key: dataField }
      }
    }
  }

  csvDataPlain = function* (shownData, levels = []) {
    for (let row of shownData) {
      const { key, ...remains } = row

      if (row.children && row.children.length) {
        const newLevels = [...levels, { [key]: this.convertValueCsvData(row) }]
        yield* this.csvDataPlain(row.children, newLevels)
      } else {
        yield Object.assign({ [key]: this.convertValueCsvData(row), ...this.convertMetricCsvData(remains) }, ...levels)
      }
    }
  }

  csvDataNested = function* (shownData) {
    for (let row of shownData) {
      const { key, ...remains } = row

      yield {
        [key]: this.convertValueCsvData(row),
        ...this.convertMetricCsvData(remains),
      }

      if (row.children) {
        yield* this.csvDataNested(row.children)
      }
    }
  }

  convertMetricCsvData = metrics => {
    if (this.state.comparison?.compare) {
      const { columns: [_, ...columns] } = this
      const comparisonMetrics = {}

      for (const { dataField } of columns) {
        const { valueA, valueB } = metrics[dataField] || {}
        if (valueA !== undefined && valueB !== undefined) {
          comparisonMetrics[`${dataField}A`] = valueA
          comparisonMetrics[`${dataField}B`] = valueB
        }
      }
      return comparisonMetrics
    }

    return metrics
  }

  RunButton = () => {
    const { startDate, endDate } = this.state
    return <RunButton onClick={() => this.handleChangeDates({ startDate, endDate })} isLoading={this.store.collection.isLoading} disabled={this.store.collection.isLoading} />
  }

  HeaderButtons = () => <>
    <this.DatesButtons />
    <HeaderSeparator />
    <this.PermanentLink />
    <this.Configure />
    <this.Export />
    <this.RunButton />
  </>

  PermanentLink = ({ id = 'report-perm-link' }) => {
    const query = useQueryparams(true)

    const parseState = hash => {
      try {
        const decoded = typeof atob === 'function'
          ? atob(hash)
          : Buffer.from(hash, 'base64').toString()
        const { groupby, sortby, metrics, filterby, startDate, endDate } = JSON.parse(decoded)

        this.setState(state => {
          const timezone = state.startDate?.tz?.() || state.endDate?.tz?.() || moment.tz.guess()

          return {
            startDate: startDate ? moment.utc(startDate).tz(timezone) : state.startDate,
            endDate: endDate ? moment.utc(endDate).tz(timezone) : state.endDate,
            ...JSON.parse(JSON.stringify({ groupby, sortby, metrics, filterby })),
          }
        }, this.loadDataState)

      } catch (e) { }
    }

    const dumpState = () => {
      const { groupby, sortby, metrics, filterby, startDate, endDate } = this.state
      return btoa(JSON.stringify({ groupby, sortby, metrics, filterby, startDate, endDate }))
    }

    useEffect(() => {
      if (query && query.configure) {
        parseState(query.configure)
      }
    }, [query])

    const URLView = ({ id }) => {
      useEffect(() => {
        const input = document.querySelector(`#${id}`)

        const a = document.createElement('a')
        a.href = history.createHref({
          ...history.location,
          search: queryString.stringify({ configure: dumpState() })
        })

        input.value = a.href

        input.select()
        document.execCommand('copy')
      })

      return <>
        <Input id={id} readOnly />
        <small className='twy-text-base'>Copied!</small>
      </>
    }

    return <DefaultPopover
      trigger={<PermanentLinkButton />}
    >
      Permanent Link
      <Separator className='m-0 my-2' />
      <URLView id={`${id}-input`} />
    </DefaultPopover>
  }

  Configure = () => {
    const { reportView } = this.useNewStore()
    const { groupby, sortby, metrics, filterby } = this.state

    if (this.configure)
      return <Configure key='filter' store={reportView} configure={this.configure} onApply={this.handleChangeConfigure} onSelect={this.handleChangeSelectConfigure} value={{ groupby, sortby, metrics, filterby }} suppressGroupBy={this.suppressGroupBy} />
    return null
  }

  Export = () => {
    const { state: { startDate, endDate } } = this

    if (!startDate || !endDate)
      return null

    return <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="lg" variant='outline'>
            Export
            <ChevronDownIcon size='16' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <this.ExportContent />
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  }

  ExportButton = ({ headers, data, description }) => {
    const { title, state: { startDate, endDate } } = this
    return <DropdownMenuItem>
      <CSVLink
        id="export"
        target="_blank"
        headers={headers}
        data={data}
        filename={`${title} - ${description} - ${startDate.format('x')}-${endDate.format('x')}.csv`}
      >
        {description}
      </CSVLink>
    </DropdownMenuItem>
  }

  ExportContent = () => {
    const [headers, dataPlain, dataNested] = useMemo(() => this.csv, [])

    return <>
      <this.ExportButton data={dataNested} headers={headers} description='Nested' />
      <this.ExportButton data={dataPlain} headers={headers} description='Plain' />
    </>
  }

  Summary = () => null

  Header = () => <>
    <HeaderContainerGrid title={`${this.title} Report`} titleClass='hidden md:block'>
      <HeaderActions>
        <this.HeaderButtons />
      </HeaderActions>
    </HeaderContainerGrid>
    <this.Summary />
  </>

  render() {
    return <>
      <Helmet title={this.title} />
      <this.ReportProvider>
        <this.Header />
        <ReportBody hasSummaryCards={!!this.summaryCards}>
          <this.Table />
        </ReportBody>
      </this.ReportProvider>
    </>
  }
}
